import { useEffect, useRef, useState } from "react";
import { MAX_DATA_POINTS } from "../utils/constants";
import { getApiHeaders } from "../utils/api";

/**
 * Custom hook for managing WebSocket connection and real-time data
 * @param {string} apiBaseUrl - Base URL for the API
 * @returns {Object} { liveData, timeSeriesData, wsConnected }
 */
function useWebSocket(apiBaseUrl) {
  const [liveData, setLiveData] = useState({});
  const [timeSeriesData, setTimeSeriesData] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // Fetch recent historical data on mount to preload charts
  useEffect(() => {
    const fetchRecentData = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/recent?limit=${MAX_DATA_POINTS}`,
          {
            headers: getApiHeaders(),
          }
        );
        if (!response.ok) {
          console.error("Failed to fetch recent data");
          return;
        }
        const recentData = await response.json();

        // Initialize time series data with historical values
        const initialTimeSeriesData = {};
        Object.entries(recentData).forEach(([tag, series]) => {
          if (Array.isArray(series) && series.length > 0) {
            initialTimeSeriesData[tag] = series;
          }
        });

        setTimeSeriesData(initialTimeSeriesData);

        // Also set the latest value as initial liveData
        const latestData = {};
        Object.entries(recentData).forEach(([tag, series]) => {
          if (Array.isArray(series) && series.length > 0) {
            const latestPoint = series[series.length - 1];
            latestData[tag] = {
              timestamp: latestPoint.timestamp,
              value: latestPoint.value,
            };
          }
        });
        setLiveData(latestData);
      } catch (err) {
        console.error("Error fetching recent data:", err);
      }
    };

    fetchRecentData();
  }, [apiBaseUrl]);

  // WebSocket connection
  useEffect(() => {
    // Determine WebSocket URL based on whether apiBaseUrl is relative or absolute
    let wsUrl;
    if (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://")) {
      // Absolute URL - extract host
      wsUrl = `ws://${apiBaseUrl
        .replace("http://", "")
        .replace("https://", "")
        .replace("/api", "")}/api/ws/stream`;
    } else {
      // Relative URL - use current host
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      wsUrl = `${protocol}//${window.location.host}/api/ws/stream`;
    }

    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setLiveData((prevData) => ({ ...prevData, ...data }));

            // Update time series data for charts
            setTimeSeriesData((prevData) => {
              const newData = { ...prevData };

              Object.entries(data).forEach(([tag, dataPoint]) => {
                if (!newData[tag]) {
                  newData[tag] = [];
                }

                // Add new data point
                newData[tag] = [
                  ...newData[tag],
                  {
                    timestamp: dataPoint.timestamp,
                    value: dataPoint.value,
                  },
                ];

                // Keep only last MAX_DATA_POINTS
                if (newData[tag].length > MAX_DATA_POINTS) {
                  newData[tag] = newData[tag].slice(-MAX_DATA_POINTS);
                }
              });

              return newData;
            });
          } catch (err) {
            console.error("Error parsing WebSocket message:", err);
          }
        };

        ws.onerror = (err) => {
          console.error("WebSocket error:", err);
          setWsConnected(false);
        };

        ws.onclose = () => {
          setWsConnected(false);
          setTimeout(connectWebSocket, 3000);
        };

        wsRef.current = ws;
      } catch (err) {
        console.error("Error creating WebSocket:", err);
        setTimeout(connectWebSocket, 3000);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [apiBaseUrl]);

  return { liveData, timeSeriesData, wsConnected };
}

export default useWebSocket;
